import { IDataflowWorkloadPayload, ModelConstants } from 'shell-models-lib';

/**
 * Workload payload parsing utilities for the Fabric Dataflow refresh activity.
 *
 * Two on-disk formats are supported:
 *  - Legacy ("MappingDataflow"): the entire payload is a single MDF.
 *      { formatVersion, type: "MappingDataflow", mappingDataflowContent: "<MDF JSON>" }
 *  - Native ("Dataflow Gen2"): an editing-session document
 *    whose `mashupDocument` is a Power Query (M) script. MDF queries appear as
 *    `[ActionType = "MDF"] shared #"<query name>" = "<MDF JSON>";` blocks within it.
 *
 * The legacy format is being deprecated. All new-format handling is concentrated in this
 * file so it can be kept while the legacy branch can be removed in a single place later.
 */

// --- Constants ---

export const DATAFLOW_PAYLOAD_LEGACY_TYPE = 'MappingDataflow';

// --- New (native) payload types ---

export interface IDataflowNewWorkloadPayload {
    formatVersion: string;
    editingSession: IDataflowEditingSession;
    externalActions?: IDataflowExternalAction[];
    type?: string;
}

/** An external action entry from the native payload's `externalActions` array. */
export interface IDataflowExternalAction {
    actionType?: string | null;
    itemType?: string | null;
    queryName: string;
    externalResourceContent: string;
}

export interface IDataflowEditingSession {
    computeEngineSettings?: Record<string, unknown>;
    editingSessionMashup: IDataflowEditingSessionMashup;
}

export interface IDataflowEditingSessionMashup {
    mashupName?: string;
    mashupDocument: string;
    queryGroups?: unknown[];
    documentLocale?: string;
    queriesMetadata?: Record<string, IDataflowQueryMetadata>;
    [key: string]: unknown;
}

export interface IDataflowQueryMetadata {
    queryId?: string;
    queryName?: string;
    lastKnownIsParameter?: boolean;
    isHidden?: boolean;
    [key: string]: unknown;
}

/** A single MDF query extracted from a native mashup document. */
export interface IExtractedMdfQuery {
    queryName: string;
    /** The JSON text of the MDF (i.e. `{ name, properties: { type: 'MappingDataFlow', ... } }`). */
    mdfContent: string;
}

// --- Format detection ---

export function isNewDataflowPayload(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload: any
): payload is IDataflowNewWorkloadPayload {
    return !!payload
        && typeof payload === 'object'
        && !!payload.editingSession?.editingSessionMashup?.mashupDocument
        && typeof payload.editingSession.editingSessionMashup.mashupDocument === 'string';
}

export function isLegacyMappingDataflowPayload(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload: any
): payload is IDataflowWorkloadPayload {
    return !!payload
        && typeof payload === 'object'
        && payload.type === DATAFLOW_PAYLOAD_LEGACY_TYPE
        && typeof payload.mappingDataflowContent === 'string';
}

// --- Mashup document parsing ---

/**
 * Extract MDF queries from the `externalActions` array when present.
 * Each entry with `actionType === "MDF"` provides the query name and
 * the raw MDF JSON content in `externalResourceContent`.
 *
 * Returns an empty array if `externalActions` is absent or has no MDF entries.
 */
export function extractMdfQueriesFromExternalActions(
    externalActions: IDataflowExternalAction[] | undefined
): IExtractedMdfQuery[] {
    if (!externalActions?.length) {
        return [];
    }
    const results: IExtractedMdfQuery[] = [];
    for (const action of externalActions) {
        const type = action.itemType?.trim() ? action.itemType : action.actionType;
        if (type === 'MDF' && action.queryName) {
            results.push({
                queryName: action.queryName,
                mdfContent: action.externalResourceContent || ''
            });
        }
    }
    return results;
}

/**
 * Extract every `[ActionType = "MDF" ...] shared <name> = "<content>";` block from a
 * mashup document.
 *
 * The MDF JSON inside the document is doubly-quoted (every `"` becomes `""`) to be a
 * valid Power Query string literal. This function unescapes that form back into raw JSON.
 *
 * Returned MDF blocks preserve their original order in the document.
 *
 * Prefer {@link extractMdfQueriesFromExternalActions} when the payload has an
 * `externalActions` array — it avoids regex and provides clean JSON directly.
 * Fall back to this function for older payloads without `externalActions`.
 */
export function extractMdfQueriesFromMashupDocument(mashupDocument: string): IExtractedMdfQuery[] {
    if (!mashupDocument) {
        return [];
    }

    const results: IExtractedMdfQuery[] = [];

    // [ActionType = "MDF", <optional extra attributes>] shared <name> = "<content>";
    // or [ItemType = "MDF", <optional extra attributes>] shared <name> = "<content>";
    // The annotation bracket may use ActionType or ItemType to tag MDF queries,
    // and may contain additional key-value pairs (e.g. ExternalResourceName).
    // Names may be bare identifiers (Mdf1) or M quoted identifiers (#"Spark transform").
    // The content string uses `""` for escaped double quotes inside M string literals.
    const blockRegex = /\[\s*(?:ActionType|ItemType)\s*=\s*"MDF"[^\]]*\]\s*shared\s+(?:#"([^"]+)"|([A-Za-z_][\w.]*))\s*=\s*"((?:[^"]|"")*)"\s*;/g;

    let match: RegExpExecArray | null;
    while ((match = blockRegex.exec(mashupDocument)) !== null) {
        const queryName = match[1] || match[2];
        const escapedContent = match[3];
        // Reverse the M-string escaping: `""` -> `"`.
        const mdfContent = escapedContent.replace(/""/g, '"');
        results.push({ queryName, mdfContent });
    }

    return results;
}

/**
 * True if the mashup document contains at least one non-MDF Power Query (M) `shared` block.
 *
 * Used by the activity UI to decide whether to offer the "Run all M queries" option:
 * a document containing only MDF queries should not show it.
 */
export function hasMQueriesInMashupDocument(mashupDocument: string): boolean {
    if (!mashupDocument) {
        return false;
    }
    // M `shared` declarations come in two forms:
    //   shared #"<quoted name>" = ...;
    //   shared <bareIdentifier> = ...;
    // We count both, then subtract those tagged as `[ActionType = "MDF" ...]` or `[ItemType = "MDF" ...]`.
    // The annotation bracket may contain additional key-value pairs (e.g. ExternalResourceName).
    const sharedRegex = /\bshared\s+(?:#"[^"]+"|[A-Za-z_][\w.]*)\s*=/g;
    const mdfSharedRegex = /\[\s*(?:ActionType|ItemType)\s*=\s*"MDF"[^\]]*\]\s*shared\s+(?:#"[^"]+"|[A-Za-z_][\w.]*)\s*=/g;
    const sharedCount = (mashupDocument.match(sharedRegex) ?? []).length;
    const mdfCount = (mashupDocument.match(mdfSharedRegex) ?? []).length;
    return sharedCount > mdfCount;
}

/**
 * Resolves the raw MDF JSON content for a given activity selection from a parsed
 * workload payload. Handles both formats:
 *  - LEGACY-MDF: returns the single implicit `mappingDataflowContent`.
 *  - New: extracts MDF queries (preferring `externalActions`, falling back to the
 *    mashup document) and returns the one matching `sparkTransformName`, or the first
 *    MDF query when no name is supplied / matched.
 *
 * @returns The raw MDF JSON string, or `null` when the payload contains no MDF.
 */
export function resolveMdfContentFromPayload(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload: any,
    sparkTransformName?: string
): string | null {
    // LEGACY-MDF: single implicit MDF. Remove with legacy support.
    if (isLegacyMappingDataflowPayload(payload)) {
        return payload.mappingDataflowContent ?? null;
    }

    if (isNewDataflowPayload(payload)) {
        const mashupDocument = payload.editingSession.editingSessionMashup.mashupDocument;
        const externalMdfQueries = payload.externalActions?.length
            ? extractMdfQueriesFromExternalActions(payload.externalActions)
            : [];
        const mdfQueries = externalMdfQueries.length
            ? externalMdfQueries
            : extractMdfQueriesFromMashupDocument(mashupDocument);
        if (!mdfQueries.length) {
            return null;
        }
        const selected = sparkTransformName
            ? mdfQueries.find((q) => q.queryName === sparkTransformName)
            : undefined;
        return (selected ?? mdfQueries[0]).mdfContent;
    }

    return null;
}

// --- Activity-level MDF mode detection ---

/** Subset of a `DataflowRefreshActivity` shape needed to determine MDF mode. */
export interface IDataflowActivityMdfShape {
    dataflowType?: string;
    sparkTransformName?: string;
    executeMashupQueriesOnly?: boolean;
}

/**
 * True when the dataflow refresh activity targets a Mapping Data Flow (MDF) — either:
 *  - LEGACY-MDF: `dataflowType === 'MappingDataflowFabric'` (single implicit MDF), or
 *  - New: `dataflowType === 'DataflowFabric'` with a specific MDF query selected
 *    via `sparkTransformName` (and `executeMashupQueriesOnly` not set).
 *
 * Centralized so MDF-only behavior (parameter validation, trace level, staging path,
 * monitoring resource fetch, etc.) can be toggled in one place. Once legacy support is
 * removed, the legacy branch can be deleted from this helper without touching call sites.
 */
export function isMdfDataflowActivity(activity: IDataflowActivityMdfShape | null | undefined): boolean {
    if (!activity) {
        return false;
    }
    // LEGACY-MDF: legacy single-MDF discriminator. Remove with legacy support.
    if (activity.dataflowType === ModelConstants.mappingDataflowfabricPipelineDataflowType) {
        return true;
    }
    return activity.dataflowType === 'DataflowFabric'
        && !activity.executeMashupQueriesOnly
        && !!activity.sparkTransformName;
}
